import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, BookOpen, CircleDot,
  FileText, FlaskConical, GitMerge, Layers3, Lightbulb,
  MessageCircleWarning, Ruler, Scale, ShieldAlert, Target, Wrench,
} from "lucide-react";
import { cn } from "@/lib/cn";

type ViewMode = "clean" | "inspect" | "debate";
type Strand = "all" | "attention" | "access" | "tradeoff";
type Focus = { kind: "node" | "edge"; id: string };
type NodeRole = "evidence" | "foundation" | "criterion" | "inference" | "policy" | "thesis";
type EdgeRole = "evidences" | "supports" | "qualifies" | "depends";
type CritiqueRole = "counter" | "undercut" | "repair";
type Point = { x: number; y: number };

type LabNode = {
  id: string;
  role: NodeRole;
  strand: Exclude<Strand, "all">;
  title: string;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

type LabEdge = {
  id: string;
  source: string;
  target: string;
  role: EdgeRole;
  strand: Exclude<Strand, "all">;
  label: string;
};

type EdgeRoute = {
  points: Point[];
  label: Point;
  pips?: Point;
};

type Critique = {
  id: string;
  role: CritiqueRole;
  anchor: Focus;
  title: string;
  text: string;
  x: number;
  y: number;
};

const NODES: LabNode[] = [
  {
    id: "ev-distraction",
    role: "evidence",
    strand: "attention",
    title: "Observed pattern",
    text: "Classroom phone access is associated with more interruptions and task switching.",
    x: 42,
    y: 116,
    w: 260,
    h: 112,
  },
  {
    id: "ev-learning",
    role: "evidence",
    strand: "attention",
    title: "Outcome evidence",
    text: "Some studies report better recall or test performance when access is restricted.",
    x: 42,
    y: 282,
    w: 260,
    h: 110,
  },
  {
    id: "access-cases",
    role: "foundation",
    strand: "access",
    title: "Access cases",
    text: "Some students use phones for translation, disability support, family care, or urgent contact.",
    x: 42,
    y: 462,
    w: 270,
    h: 118,
  },
  {
    id: "criterion",
    role: "criterion",
    strand: "tradeoff",
    title: "Decision rule",
    text: "A school rule is justified only if attention gains beat enforcement, trust, and access costs.",
    x: 42,
    y: 636,
    w: 282,
    h: 118,
  },
  {
    id: "interruptions",
    role: "inference",
    strand: "attention",
    title: "Interpretation",
    text: "Reducing immediate phone availability should lower interruption pressure during class.",
    x: 381,
    y: 126,
    w: 274,
    h: 116,
  },
  {
    id: "learning-gain",
    role: "inference",
    strand: "attention",
    title: "Inference",
    text: "Fewer interruptions plausibly translate into more usable attention and better learning time.",
    x: 381,
    y: 298,
    w: 274,
    h: 118,
  },
  {
    id: "exceptions",
    role: "inference",
    strand: "access",
    title: "Exception design",
    text: "Office, medical, translation, and caregiver exceptions can preserve necessary access.",
    x: 381,
    y: 484,
    w: 284,
    h: 118,
  },
  {
    id: "tradeoff",
    role: "inference",
    strand: "tradeoff",
    title: "Tradeoff test",
    text: "The policy should be judged by net attention gain, not by whether phones are bad in general.",
    x: 395,
    y: 662,
    w: 292,
    h: 116,
  },
  {
    id: "pouches",
    role: "policy",
    strand: "attention",
    title: "Mechanism",
    text: "Locked pouches reduce casual access without requiring teachers to negotiate phone use all period.",
    x: 750,
    y: 214,
    w: 294,
    h: 122,
  },
  {
    id: "scoped-policy",
    role: "policy",
    strand: "access",
    title: "Scoped implementation",
    text: "The rule has to include fast exceptions, opt-outs where legally required, and published success criteria.",
    x: 762,
    y: 492,
    w: 306,
    h: 128,
  },
  {
    id: "thesis",
    role: "thesis",
    strand: "tradeoff",
    title: "Conclusion",
    text: "Schools should use locked phone pouches during instructional time, with office and accessibility exceptions.",
    x: 1158,
    y: 346,
    w: 320,
    h: 146,
  },
];

const EDGES: LabEdge[] = [
  { id: "e-distraction-interruptions", source: "ev-distraction", target: "interruptions", role: "evidences", strand: "attention", label: "pattern supports interpretation" },
  { id: "e-learning-claim", source: "ev-learning", target: "learning-gain", role: "evidences", strand: "attention", label: "learning signal" },
  { id: "e-interruptions-pouches", source: "interruptions", target: "pouches", role: "supports", strand: "attention", label: "less access, fewer interruptions" },
  { id: "e-learning-pouches", source: "learning-gain", target: "pouches", role: "supports", strand: "attention", label: "attention gain" },
  { id: "e-access-exceptions", source: "access-cases", target: "exceptions", role: "qualifies", strand: "access", label: "access constraints" },
  { id: "e-exceptions-scoped", source: "exceptions", target: "scoped-policy", role: "supports", strand: "access", label: "exception path" },
  { id: "e-criterion-tradeoff", source: "criterion", target: "tradeoff", role: "depends", strand: "tradeoff", label: "sets evaluation standard" },
  { id: "e-tradeoff-scoped", source: "tradeoff", target: "scoped-policy", role: "qualifies", strand: "tradeoff", label: "must pass costs test" },
  { id: "e-pouches-thesis", source: "pouches", target: "thesis", role: "supports", strand: "attention", label: "mechanism supports conclusion" },
  { id: "e-scoped-thesis", source: "scoped-policy", target: "thesis", role: "supports", strand: "access", label: "scope makes policy acceptable" },
  { id: "e-criterion-thesis", source: "criterion", target: "thesis", role: "qualifies", strand: "tradeoff", label: "criterion frames conclusion" },
];

const CRITIQUES: Critique[] = [
  {
    id: "c-method",
    role: "undercut",
    anchor: { kind: "edge", id: "e-learning-claim" },
    title: "Measurement issue",
    text: "Some evidence measures distraction or quiet compliance, not durable learning outcomes.",
    x: 290,
    y: 246,
  },
  {
    id: "c-selection",
    role: "undercut",
    anchor: { kind: "edge", id: "e-learning-pouches" },
    title: "Causal gap",
    text: "The learning gains may come from school culture or classroom norms, not the pouch policy itself.",
    x: 690,
    y: 354,
  },
  {
    id: "c-enforcement",
    role: "counter",
    anchor: { kind: "edge", id: "e-interruptions-pouches" },
    title: "Enforcement cost",
    text: "Universal pouches can create conflict, second-device hiding, and a compliance ritual that eats class time.",
    x: 690,
    y: 102,
  },
  {
    id: "c-exception-speed",
    role: "undercut",
    anchor: { kind: "edge", id: "e-exceptions-scoped" },
    title: "Exception gap",
    text: "Office exceptions may be too slow for real-time medical, translation, or family-care needs.",
    x: 694,
    y: 642,
  },
  {
    id: "c-values",
    role: "counter",
    anchor: { kind: "edge", id: "e-tradeoff-scoped" },
    title: "Value disagreement",
    text: "A student-autonomy lens may reject the rule even if moderate attention gains are real.",
    x: 716,
    y: 764,
  },
  {
    id: "c-scope",
    role: "counter",
    anchor: { kind: "node", id: "thesis" },
    title: "Scope objection",
    text: "Elementary, middle, and high school contexts may require different rules rather than one thesis.",
    x: 1136,
    y: 526,
  },
  {
    id: "r-pilot",
    role: "repair",
    anchor: { kind: "edge", id: "e-scoped-thesis" },
    title: "Repair path",
    text: "Pilot pouches by grade band, publish success metrics, and sunset the rule if costs dominate.",
    x: 1048,
    y: 654,
  },
];

const STRANDS: { id: Strand; label: string; color: string }[] = [
  { id: "all", label: "All strands", color: "var(--color-primary)" },
  { id: "attention", label: "Attention", color: "var(--color-evidence)" },
  { id: "access", label: "Access", color: "var(--color-qualify)" },
  { id: "tradeoff", label: "Tradeoff", color: "var(--color-define)" },
];

const ROLE_META: Record<NodeRole, { label: string; icon: ReactNode; color: string; description: string }> = {
  evidence: {
    label: "Evidence",
    icon: <FileText size={13} />,
    color: "var(--color-evidence)",
    description: "A left-side empirical starting point.",
  },
  foundation: {
    label: "Foundation",
    icon: <FlaskConical size={13} />,
    color: "var(--color-qualify)",
    description: "A factual or practical condition the argument must honor.",
  },
  criterion: {
    label: "Criterion",
    icon: <Ruler size={13} />,
    color: "var(--color-define)",
    description: "The value standard used to judge the proposal.",
  },
  inference: {
    label: "Inference",
    icon: <GitMerge size={13} />,
    color: "var(--color-support)",
    description: "A middle step that turns evidence into a stronger claim.",
  },
  policy: {
    label: "Policy step",
    icon: <Wrench size={13} />,
    color: "var(--color-primary-bright)",
    description: "A proposed mechanism or scoped implementation.",
  },
  thesis: {
    label: "Thesis",
    icon: <Target size={13} />,
    color: "var(--color-primary)",
    description: "The terminal conclusion reached by the strands.",
  },
};

const EDGE_META: Record<EdgeRole, { color: string; width: number; dash?: string; label: string }> = {
  evidences: { color: "var(--color-evidence)", width: 2.2, label: "evidences" },
  supports: { color: "var(--color-support)", width: 2.8, label: "supports" },
  qualifies: { color: "var(--color-qualify)", width: 2.2, dash: "7 5", label: "qualifies" },
  depends: { color: "var(--color-inferred)", width: 2, dash: "2 6", label: "depends" },
};

const CRITIQUE_META: Record<CritiqueRole, { label: string; icon: ReactNode; color: string }> = {
  counter: { label: "Counter", icon: <ShieldAlert size={12} />, color: "var(--color-rebut)" },
  undercut: { label: "Undercut", icon: <MessageCircleWarning size={12} />, color: "var(--color-undercut)" },
  repair: { label: "Repair", icon: <Wrench size={12} />, color: "var(--color-primary-bright)" },
};

const CANVAS_W = 1500;
const CANVAS_H = 880;

const EDGE_ROUTES: Record<string, EdgeRoute> = {
  "e-distraction-interruptions": {
    points: [{ x: 302, y: 172 }, { x: 342, y: 172 }, { x: 342, y: 184 }, { x: 381, y: 184 }],
    label: { x: 342, y: 154 },
  },
  "e-learning-claim": {
    points: [{ x: 302, y: 337 }, { x: 342, y: 337 }, { x: 342, y: 357 }, { x: 381, y: 357 }],
    label: { x: 342, y: 319 },
    pips: { x: 342, y: 337 },
  },
  "e-interruptions-pouches": {
    points: [{ x: 655, y: 184 }, { x: 690, y: 184 }, { x: 690, y: 260 }, { x: 750, y: 260 }],
    label: { x: 690, y: 206 },
    pips: { x: 690, y: 236 },
  },
  "e-learning-pouches": {
    points: [{ x: 655, y: 357 }, { x: 716, y: 357 }, { x: 716, y: 304 }, { x: 750, y: 304 }],
    label: { x: 716, y: 336 },
    pips: { x: 716, y: 328 },
  },
  "e-access-exceptions": {
    points: [{ x: 312, y: 521 }, { x: 344, y: 521 }, { x: 344, y: 543 }, { x: 381, y: 543 }],
    label: { x: 344, y: 503 },
  },
  "e-exceptions-scoped": {
    points: [{ x: 665, y: 543 }, { x: 708, y: 543 }, { x: 708, y: 552 }, { x: 762, y: 552 }],
    label: { x: 708, y: 524 },
    pips: { x: 708, y: 552 },
  },
  "e-criterion-tradeoff": {
    points: [{ x: 324, y: 695 }, { x: 360, y: 695 }, { x: 360, y: 720 }, { x: 395, y: 720 }],
    label: { x: 360, y: 677 },
  },
  "e-tradeoff-scoped": {
    points: [{ x: 687, y: 720 }, { x: 734, y: 720 }, { x: 734, y: 586 }, { x: 762, y: 586 }],
    label: { x: 734, y: 684 },
    pips: { x: 734, y: 650 },
  },
  "e-pouches-thesis": {
    points: [{ x: 1044, y: 275 }, { x: 1098, y: 275 }, { x: 1098, y: 392 }, { x: 1158, y: 392 }],
    label: { x: 1098, y: 332 },
  },
  "e-scoped-thesis": {
    points: [{ x: 1068, y: 556 }, { x: 1122, y: 556 }, { x: 1122, y: 446 }, { x: 1158, y: 446 }],
    label: { x: 1122, y: 516 },
    pips: { x: 1122, y: 510 },
  },
  "e-criterion-thesis": {
    points: [{ x: 324, y: 724 }, { x: 360, y: 724 }, { x: 360, y: 856 }, { x: 1140, y: 856 }, { x: 1140, y: 472 }, { x: 1158, y: 472 }],
    label: { x: 750, y: 856 },
  },
};

function nodeCenter(node: LabNode) {
  return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
}

function roundedPolylinePath(points: Point[], radius = 14) {
  if (points.length < 2) return "";

  const [start, ...rest] = points;
  let path = `M ${start.x} ${start.y}`;

  rest.forEach((point, index) => {
    const next = rest[index + 1];
    const prev = points[index];
    if (!next) {
      path += ` L ${point.x} ${point.y}`;
      return;
    }

    const prevDistance = Math.hypot(point.x - prev.x, point.y - prev.y);
    const nextDistance = Math.hypot(next.x - point.x, next.y - point.y);
    const cornerRadius = Math.min(radius, prevDistance / 2, nextDistance / 2);
    if (cornerRadius <= 0) {
      path += ` L ${point.x} ${point.y}`;
      return;
    }

    const before = {
      x: point.x + ((prev.x - point.x) / prevDistance) * cornerRadius,
      y: point.y + ((prev.y - point.y) / prevDistance) * cornerRadius,
    };
    const after = {
      x: point.x + ((next.x - point.x) / nextDistance) * cornerRadius,
      y: point.y + ((next.y - point.y) / nextDistance) * cornerRadius,
    };

    path += ` L ${before.x} ${before.y} Q ${point.x} ${point.y} ${after.x} ${after.y}`;
  });

  return path;
}

function edgeGeometry(edge: LabEdge, nodeById: Map<string, LabNode>) {
  const source = nodeById.get(edge.source);
  const target = nodeById.get(edge.target);
  if (!source || !target) return null;

  const route = EDGE_ROUTES[edge.id];
  if (route) {
    return {
      path: roundedPolylinePath(route.points),
      mid: route.label,
      pips: route.pips ?? route.label,
      start: route.points[0],
      end: route.points[route.points.length - 1],
      source,
      target,
    };
  }

  const s = nodeCenter(source);
  const t = nodeCenter(target);
  const leftToRight = s.x <= t.x;
  const sx = leftToRight ? source.x + source.w : source.x;
  const tx = leftToRight ? target.x : target.x + target.w;
  const sy = s.y;
  const ty = t.y;
  const dx = Math.max(120, Math.abs(tx - sx) * 0.48);
  const path = `M ${sx} ${sy} C ${sx + (leftToRight ? dx : -dx)} ${sy}, ${tx - (leftToRight ? dx : -dx)} ${ty}, ${tx} ${ty}`;
  return {
    path,
    mid: { x: (sx + tx) / 2, y: (sy + ty) / 2 },
    pips: { x: (sx + tx) / 2, y: (sy + ty) / 2 },
    start: { x: sx, y: sy },
    end: { x: tx, y: ty },
    source,
    target,
  };
}

function focusMatches(a: Focus, b: Focus) {
  return a.kind === b.kind && a.id === b.id;
}

function critiquesFor(anchor: Focus) {
  return CRITIQUES.filter((critique) => focusMatches(critique.anchor, anchor));
}

function relatedNodeIds(focus: Focus) {
  const ids = new Set<string>();
  if (focus.kind === "node") {
    ids.add(focus.id);
    EDGES.forEach((edge) => {
      if (edge.source === focus.id || edge.target === focus.id) {
        ids.add(edge.source);
        ids.add(edge.target);
      }
    });
  } else {
    const edge = EDGES.find((item) => item.id === focus.id);
    if (edge) {
      ids.add(edge.source);
      ids.add(edge.target);
    }
  }

  CRITIQUES.forEach((critique) => {
    if (!focusMatches(critique.anchor, focus)) return;
    if (critique.anchor.kind === "node") ids.add(critique.anchor.id);
    if (critique.anchor.kind === "edge") {
      const edge = EDGES.find((item) => item.id === critique.anchor.id);
      if (edge) {
        ids.add(edge.source);
        ids.add(edge.target);
      }
    }
  });
  return ids;
}

function edgeTouchesFocus(edge: LabEdge, focus: Focus) {
  if (focus.kind === "edge") return edge.id === focus.id;
  return edge.source === focus.id || edge.target === focus.id;
}

function itemInStrand(strand: Strand, itemStrand: Strand) {
  return strand === "all" || strand === itemStrand;
}

export default function VisualLanguageLab() {
  const [mode, setMode] = useState<ViewMode>("clean");
  const [strand, setStrand] = useState<Strand>("all");
  const [focus, setFocus] = useState<Focus>({ kind: "edge", id: "e-interruptions-pouches" });
  const nodeById = useMemo(() => new Map(NODES.map((node) => [node.id, node])), []);
  const relatedIds = useMemo(() => relatedNodeIds(focus), [focus]);
  const selectedNode = focus.kind === "node" ? nodeById.get(focus.id) : null;
  const selectedEdge = focus.kind === "edge" ? EDGES.find((edge) => edge.id === focus.id) ?? null : null;
  const visibleCritiques = useMemo(() => (
    CRITIQUES.filter((critique) => (
      mode === "debate"
      || (mode === "inspect" && focusMatches(critique.anchor, focus))
    ))
  ), [focus, mode]);

  const activeCritiqueCount = mode === "debate" ? CRITIQUES.length : critiquesFor(focus).length;

  return (
    <div className="min-h-screen bg-strain-field text-[var(--color-ink)]">
      <header className="border-b border-[var(--color-border)] bg-[var(--color-surface)]/76 backdrop-blur">
        <div className="mx-auto max-w-[1500px] px-4 py-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="mb-3 flex items-center gap-3 text-[12px] text-[var(--color-ink-muted)]">
                <Link to="/app" className="inline-flex items-center gap-1.5 hover:text-[var(--color-ink)]">
                  <ArrowLeft size={13} /> Back to app
                </Link>
                <span className="text-[var(--color-ink-faint)]">·</span>
                <Link to="/lab/node-modifiers" className="hover:text-[var(--color-ink)]">
                  Node modifiers lab
                </Link>
              </div>
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-lg border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                  <Layers3 size={17} />
                </span>
                <div>
                  <h1 className="text-xl font-semibold tracking-tight">Foundations-to-thesis lab</h1>
                  <p className="text-[13px] text-[var(--color-ink-muted)]">
                    Phone-pouch strain rebuilt as converging strands: evidence and criteria on the left, conclusion on the right.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <ModeButton active={mode === "clean"} onClick={() => setMode("clean")} icon={<BookOpen size={14} />} label="Clean chain" />
              <ModeButton active={mode === "inspect"} onClick={() => setMode("inspect")} icon={<CircleDot size={14} />} label="Inspect crux" />
              <ModeButton active={mode === "debate"} onClick={() => setMode("debate")} icon={<MessageCircleWarning size={14} />} label="Full debate" />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1980px] gap-4 px-4 py-4 min-[1900px]:grid-cols-[minmax(0,1fr)_360px]">
        <section className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Prototype strain</div>
              <div className="text-[14px] font-semibold">Phone pouches in schools</div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {STRANDS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setStrand(item.id)}
                  className={cn(
                    "inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] transition-colors",
                    strand === item.id
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/12 text-[var(--color-ink)]"
                      : "border-[var(--color-border)] bg-[var(--color-surface-2)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
                  )}
                  style={{ color: strand === item.id ? item.color : undefined }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.color }} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-auto bg-grid">
            <div className="relative h-[880px] w-[1500px] shrink-0">
              <ColumnLabel x={62} label="Foundations" />
              <ColumnLabel x={405} label="Inferences" />
              <ColumnLabel x={790} label="Policy shape" />
              <ColumnLabel x={1194} label="Conclusion" />

              <svg className="pointer-events-none absolute inset-0 z-30 h-full w-full" viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`} aria-hidden="true">
                <defs>
                  {Object.entries(EDGE_META).map(([role, meta]) => (
                    <marker
                      key={role}
                      id={`lab-arrow-${role}`}
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="7"
                      markerHeight="7"
                      orient="auto"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill={meta.color} />
                    </marker>
                  ))}
                </defs>

                {EDGES.map((edge) => {
                  const geom = edgeGeometry(edge, nodeById);
                  if (!geom) return null;
                  const meta = EDGE_META[edge.role];
                  const selected = focus.kind === "edge" && focus.id === edge.id;
                  const strandDim = !itemInStrand(strand, edge.strand);
                  const inspectDim = mode === "inspect" && !edgeTouchesFocus(edge, focus);
                  const dimmed = strandDim || inspectDim;
                  const count = critiquesFor({ kind: "edge", id: edge.id }).length;
                  return (
                    <g key={edge.id} opacity={dimmed ? 0.18 : 1}>
                      <path
                        d={geom.path}
                        stroke="transparent"
                        strokeWidth={18}
                        fill="none"
                        className="pointer-events-auto cursor-pointer"
                        onClick={() => {
                          setFocus({ kind: "edge", id: edge.id });
                          setMode("inspect");
                        }}
                      />
                      <path
                        d={geom.path}
                        stroke={meta.color}
                        strokeWidth={selected ? meta.width + 1.4 : meta.width}
                        strokeDasharray={meta.dash}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        markerEnd={`url(#lab-arrow-${edge.role})`}
                        className="pointer-events-auto cursor-pointer"
                        onClick={() => {
                          setFocus({ kind: "edge", id: edge.id });
                          setMode("inspect");
                        }}
                      />
                      <circle
                        cx={geom.start.x}
                        cy={geom.start.y}
                        r={selected ? 4.3 : 3.5}
                        fill="var(--color-surface)"
                        stroke={meta.color}
                        strokeWidth={selected ? 2.1 : 1.6}
                        className="pointer-events-none"
                      />
                      {count > 0 && (
                        <CritiquePips
                          x={geom.pips.x}
                          y={geom.pips.y}
                          count={count}
                          active={selected}
                          onClick={() => {
                            setFocus({ kind: "edge", id: edge.id });
                            setMode("inspect");
                          }}
                        />
                      )}
                    </g>
                  );
                })}
              </svg>

              {NODES.map((node) => {
                const selected = focus.kind === "node" && focus.id === node.id;
                const strandDim = !itemInStrand(strand, node.strand);
                const inspectDim = mode === "inspect" && !relatedIds.has(node.id);
                return (
                  <LabNodeCard
                    key={node.id}
                    node={node}
                    active={selected}
                    dimmed={strandDim || inspectDim}
                    critiqueCount={critiquesFor({ kind: "node", id: node.id }).length}
                    onClick={() => {
                      setFocus({ kind: "node", id: node.id });
                      setMode("inspect");
                    }}
                  />
                );
              })}

              {visibleCritiques.map((critique) => {
                const dimmed = mode === "inspect" && !focusMatches(critique.anchor, focus);
                return (
                  <CritiqueCard
                    key={critique.id}
                    critique={critique}
                    dimmed={dimmed}
                    onClick={() => {
                      setFocus(critique.anchor);
                      setMode("inspect");
                    }}
                  />
                );
              })}
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <Panel title="Current focus" icon={<Target size={14} />}>
            {selectedEdge ? (
              <SelectedEdge edge={selectedEdge} nodeById={nodeById} />
            ) : selectedNode ? (
              <SelectedNode node={selectedNode} />
            ) : null}
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Metric value={activeCritiqueCount} label="weak spots" tone="danger" />
              <Metric value={focus.kind === "edge" ? 1 : EDGES.filter((edge) => edge.source === focus.id || edge.target === focus.id).length} label="focus links" tone="muted" />
              <Metric value={mode === "debate" ? CRITIQUES.filter((critique) => critique.role === "repair").length : critiquesFor(focus).filter((critique) => critique.role === "repair").length} label="repairs" tone="ok" />
            </div>
          </Panel>

          <Panel title="Disagreement map" icon={<Scale size={14} />}>
            <DisagreementButton
              label="Evidence dispute"
              text="Do phone studies measure learning or just quiet?"
              active={focus.kind === "edge" && focus.id === "e-learning-claim"}
              onClick={() => {
                setFocus({ kind: "edge", id: "e-learning-claim" });
                setMode("inspect");
              }}
            />
            <DisagreementButton
              label="Inference dispute"
              text="Does less phone access actually imply fewer meaningful interruptions?"
              active={focus.kind === "edge" && focus.id === "e-interruptions-pouches"}
              onClick={() => {
                setFocus({ kind: "edge", id: "e-interruptions-pouches" });
                setMode("inspect");
              }}
            />
            <DisagreementButton
              label="Access dispute"
              text="Are office exceptions fast enough for real accessibility needs?"
              active={focus.kind === "edge" && focus.id === "e-exceptions-scoped"}
              onClick={() => {
                setFocus({ kind: "edge", id: "e-exceptions-scoped" });
                setMode("inspect");
              }}
            />
            <DisagreementButton
              label="Value dispute"
              text="How much autonomy cost is worth a moderate attention gain?"
              active={focus.kind === "edge" && focus.id === "e-tradeoff-scoped"}
              onClick={() => {
                setFocus({ kind: "edge", id: "e-tradeoff-scoped" });
                setMode("inspect");
              }}
            />
          </Panel>

          <Panel title="Reading order" icon={<ArrowRight size={14} />}>
            <PathStep label="1. Start" text="Evidence, access constraints, and value criteria sit on the left." />
            <PathStep label="2. Interpret" text="Middle cards say what the left-side material implies." />
            <PathStep label="3. Shape policy" text="Mechanism and scope cards show the proposal taking form." />
            <PathStep label="4. Conclude" text="The thesis is the endpoint, not the root." />
          </Panel>

          <Panel title="Legend" icon={<Lightbulb size={14} />}>
            <LegendRow role="evidence" />
            <LegendRow role="criterion" />
            <LegendRow role="inference" />
            <LegendRow role="policy" />
            <LegendRow role="thesis" />
            <div className="mt-3 border-t border-[var(--color-border)] pt-3">
              {(["counter", "undercut", "repair"] as CritiqueRole[]).map((role) => (
                <div key={role} className="mb-2 flex items-start gap-2 last:mb-0">
                  <span className="mt-0.5" style={{ color: CRITIQUE_META[role].color }}>{CRITIQUE_META[role].icon}</span>
                  <div>
                    <div className="text-[12px] font-semibold">{CRITIQUE_META[role].label}</div>
                    <div className="text-[11px] leading-relaxed text-[var(--color-ink-muted)]">
                      {role === "counter" ? "Challenges a claim or policy step." : role === "undercut" ? "Challenges an inference between two cards." : "Suggests a fix that could rescue the path."}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </aside>
      </main>
    </div>
  );
}

function ModeButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[12px] transition-colors",
        active
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/12 text-[var(--color-primary-bright)]"
          : "border-[var(--color-border)] bg-[var(--color-surface-2)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
      )}
    >
      {icon}{label}
    </button>
  );
}

function ColumnLabel({ x, label }: { x: number; label: string }) {
  return (
    <div
      className="pointer-events-none absolute top-7 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)]/90 px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]"
      style={{ left: x }}
    >
      {label}
    </div>
  );
}

function LabNodeCard({
  node, active, dimmed, critiqueCount, onClick,
}: {
  node: LabNode;
  active: boolean;
  dimmed: boolean;
  critiqueCount: number;
  onClick: () => void;
}) {
  const meta = ROLE_META[node.role];
  const isThesis = node.role === "thesis";
  const isEvidence = node.role === "evidence";
  const isCriterion = node.role === "criterion";
  const isInference = node.role === "inference";
  const style = {
    left: node.x,
    top: node.y,
    width: node.w,
    minHeight: node.h,
  } satisfies CSSProperties;

  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={cn(
        "absolute z-20 text-left transition-all duration-200",
        active && "z-40 scale-[1.015]",
        dimmed && "opacity-28",
      )}
    >
      <div
        className={cn(
          "relative h-full border bg-[var(--color-surface)] p-3 shadow-card",
          isThesis && "rounded-xl border-[var(--color-primary)]/75 bg-[color-mix(in_srgb,var(--color-primary)_11%,var(--color-surface)_89%)] shadow-glow-primary",
          node.role === "foundation" && "rounded-md border-[var(--color-qualify)]/55",
          isEvidence && "rounded-md border-[var(--color-evidence)]/55",
          isCriterion && "rounded-lg border-[var(--color-define)]/60 bg-[color-mix(in_srgb,var(--color-define)_7%,var(--color-surface)_93%)]",
          isInference && "rounded-xl border-[var(--color-support)]/45",
          node.role === "policy" && "rounded-lg border-[var(--color-primary)]/50 bg-[color-mix(in_srgb,var(--color-primary)_7%,var(--color-surface)_93%)]",
          active && "ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-base)]",
        )}
      >
        {isEvidence && (
          <span className="absolute right-0 top-0 h-5 w-5 border-b border-l border-[var(--color-evidence)]/50 bg-[var(--color-surface-2)]" />
        )}
        <div className="mb-2 flex items-center gap-1.5">
          <span
            className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider"
            style={{
              color: meta.color,
              borderColor: `color-mix(in srgb, ${meta.color} 44%, var(--color-border) 56%)`,
              background: `color-mix(in srgb, ${meta.color} 10%, var(--color-surface) 90%)`,
            }}
          >
            {meta.icon}{meta.label}
          </span>
          {isThesis && <span className="ml-auto text-[9px] font-mono uppercase tracking-wider text-[var(--color-primary)]">endpoint</span>}
          {critiqueCount > 0 && (
            <span className={cn(
              "inline-flex h-5 items-center gap-0.5 rounded-full border border-[var(--color-rebut)] bg-[var(--color-surface)] px-1.5",
              isThesis ? "ml-1" : "ml-auto",
            )}>
              {Array.from({ length: critiqueCount }).map((_, index) => (
                <span key={index} className="h-1.5 w-1.5 rounded-full bg-[var(--color-rebut)]" />
              ))}
            </span>
          )}
        </div>
        <div className="mb-1 text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">{node.title}</div>
        <div className={cn(
          "leading-snug",
          isThesis ? "text-[15px] font-semibold text-[var(--color-ink)]" : "text-[13px] text-[var(--color-ink-soft)]",
        )}>
          {node.text}
        </div>
      </div>
    </button>
  );
}

function CritiquePips({
  x, y, count, active, onClick,
}: {
  x: number;
  y: number;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <foreignObject x={x - 18} y={y - 10} width="40" height="22" className="pointer-events-auto">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex h-5 min-w-8 items-center justify-center gap-0.5 rounded-full border bg-[var(--color-surface)] px-1 shadow-card transition-colors",
          active ? "border-[var(--color-primary)]" : "border-[var(--color-rebut)] hover:bg-[color-mix(in_srgb,var(--color-rebut)_10%,var(--color-surface)_90%)]",
        )}
        title={`${count} critique${count === 1 ? "" : "s"}`}
      >
        {Array.from({ length: Math.min(count, 4) }).map((_, index) => (
          <span key={index} className="h-1.5 w-1.5 rounded-full bg-[var(--color-rebut)]" />
        ))}
      </button>
    </foreignObject>
  );
}

function CritiqueCard({ critique, dimmed, onClick }: { critique: Critique; dimmed: boolean; onClick: () => void }) {
  const meta = CRITIQUE_META[critique.role];
  const style = {
    left: critique.x,
    top: critique.y,
    width: critique.role === "repair" ? 308 : 282,
  } satisfies CSSProperties;

  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={cn(
        "absolute z-50 text-left transition-all duration-200",
        dimmed && "opacity-30",
      )}
    >
      <div
        className={cn(
          "rounded-sm border bg-[var(--color-surface)] p-2.5 shadow-pop",
          critique.role === "repair" ? "rounded-lg" : "rounded-sm",
        )}
        style={{
          borderColor: `color-mix(in srgb, ${meta.color} 58%, var(--color-border) 42%)`,
          background: `color-mix(in srgb, ${meta.color} 9%, var(--color-surface) 91%)`,
        }}
      >
        <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider" style={{ color: meta.color }}>
          {meta.icon}
          <span>{meta.label}</span>
          <span className="text-[var(--color-ink-faint)]">/ {critique.title}</span>
        </div>
        <div className="text-[12px] leading-[1.38] text-[var(--color-ink)]">{critique.text}</div>
      </div>
    </button>
  );
}

function SelectedNode({ node }: { node: LabNode }) {
  const meta = ROLE_META[node.role];
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
        {meta.icon} {meta.label}
      </div>
      <div className="text-[14px] font-semibold leading-snug">{node.text}</div>
      <div className="mt-2 text-[12px] leading-relaxed text-[var(--color-ink-muted)]">{meta.description}</div>
    </div>
  );
}

function SelectedEdge({ edge, nodeById }: { edge: LabEdge; nodeById: Map<string, LabNode> }) {
  const meta = EDGE_META[edge.role];
  const source = nodeById.get(edge.source);
  const target = nodeById.get(edge.target);
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
        <ArrowRight size={12} /> {meta.label}
      </div>
      <div className="space-y-2">
        <Endpoint label="from" text={source?.text ?? edge.source} />
        <Endpoint label="to" text={target?.text ?? edge.target} />
      </div>
      <div className="mt-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">
        {edge.label}
      </div>
    </div>
  );
}

function Endpoint({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[9px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">{label}</div>
      <div className="text-[12px] leading-snug text-[var(--color-ink-soft)]">{text}</div>
    </div>
  );
}

function Panel({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-card">
      <div className="mb-3 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">
        {icon}{title}
      </div>
      {children}
    </section>
  );
}

function Metric({ value, label, tone }: { value: number; label: string; tone: "danger" | "ok" | "muted" }) {
  const color =
    tone === "danger" ? "text-[var(--color-rebut)]"
    : tone === "ok" ? "text-[var(--color-support)]"
    : "text-[var(--color-ink-muted)]";
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-2">
      <div className={cn("font-mono text-lg font-semibold", color)}>{value}</div>
      <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">{label}</div>
    </div>
  );
}

function DisagreementButton({
  label, text, active, onClick,
}: {
  label: string;
  text: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "mb-2 w-full rounded-lg border p-2.5 text-left transition-colors last:mb-0",
        active
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
          : "border-[var(--color-border)] bg-[var(--color-surface-2)] hover:border-[var(--color-primary)]",
      )}
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-secondary)]">{label}</div>
      <div className="mt-1 text-[12px] leading-snug text-[var(--color-ink-soft)]">{text}</div>
    </button>
  );
}

function PathStep({ label, text }: { label: string; text: string }) {
  return (
    <div className="mb-2 flex gap-2 last:mb-0">
      <span className="mt-0.5 h-5 shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-surface-2)] px-1.5 text-[9px] font-mono uppercase tracking-wider text-[var(--color-primary)]">
        {label}
      </span>
      <span className="text-[12px] leading-relaxed text-[var(--color-ink-soft)]">{text}</span>
    </div>
  );
}

function LegendRow({ role }: { role: NodeRole }) {
  const meta = ROLE_META[role];
  return (
    <div className="mb-2 flex items-start gap-2 last:mb-0">
      <span className="mt-0.5" style={{ color: meta.color }}>{meta.icon}</span>
      <div>
        <div className="text-[12px] font-semibold">{meta.label}</div>
        <div className="text-[11px] leading-relaxed text-[var(--color-ink-muted)]">{meta.description}</div>
      </div>
    </div>
  );
}
